import { Base64 } from 'js-base64';
import { get } from 'lodash';

import type { MaasModelSource, MaasModelSourceType } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';
import { MaasModelSourceType as SourceTypeEnum } from '@/delivery-type-plugin/capabilities/maas-init/maasTypes';

import type { MaasModel, MaasModelSourceEntry } from './modelInstanceTypes';
import { hasModelSourceCore } from './modelSource';
import { buildExistingPvcModelSource } from './modelSourcePlaceholder';
import { parseStorageQuantityToGi } from './quantity';

/** 模型实例「添加新模型」最多条数 */
export const MAX_MODEL_SOURCES = 6;

/** 表单：添加新模型 / 添加已有模型 */
export type ModelStorageAddMode = 'new' | 'existing';

export const MODEL_STORAGE_MODE_FIELD = '_modelStorageMode';

const SOURCE_TYPE_ORDER: MaasModelSourceType[] = [
  SourceTypeEnum.Http,
  SourceTypeEnum.S3,
  SourceTypeEnum.ModelScope,
  SourceTypeEnum.HuggingFace,
  SourceTypeEnum.StackScope,
  SourceTypeEnum.ModelPlaza,
];

function encodeS3Credential(value: unknown): string {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return Base64.encode(raw);
}

function buildSanitizedModelSourceByType(source: Record<string, unknown>, activeType: MaasModelSourceType): MaasModelSource | undefined {
  const result: MaasModelSource = {};
  switch (activeType) {
    case SourceTypeEnum.Http: {
      const url = get(source, 'http.url');
      if (url) result.http = { url: String(url) };
      break;
    }
    case SourceTypeEnum.S3: {
      const url = get(source, 's3.url');
      const accessKeyID = encodeS3Credential(get(source, 's3.secretRef.accessKeyID'));
      const secretKey = encodeS3Credential(get(source, 's3.secretRef.secretKey'));
      if (!url) break;
      result.s3 = {
        url: String(url),
        secretRef: { accessKeyID, secretKey },
      };
      break;
    }
    case SourceTypeEnum.ModelScope: {
      const model = get(source, 'modelscope.model');
      if (model) result.modelscope = { model: String(model) };
      break;
    }
    case SourceTypeEnum.HuggingFace: {
      const model = get(source, 'huggingface.model');
      if (model) result.huggingface = { model: String(model) };
      break;
    }
    case SourceTypeEnum.StackScope: {
      const model = get(source, 'stackscope.model');
      const url = get(source, 'stackscope.url');
      if (model && url) result.stackscope = { model: String(model), url: String(url) };
      break;
    }
    case SourceTypeEnum.ModelPlaza: {
      const model = get(source, 'modelplaza.model');
      const url = get(source, 'modelplaza.url');
      if (model && url) result.modelplaza = { model: String(model), url: String(url) };
      break;
    }
    default:
      break;
  }
  return Object.keys(result).length ? result : undefined;
}

/** 从条目或嵌套 source 对象提取可 sanitize 的来源字段 */
export function extractSourceRecordFromEntry(entry: Record<string, unknown>): Record<string, unknown> {
  const nested = entry.source;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return nested as Record<string, unknown>;
  }
  const { name: _n, size: _s, ...rest } = entry;
  return rest;
}

export function sanitizeModelSource(source?: Record<string, unknown>): MaasModelSource | undefined {
  if (!source) return undefined;
  for (const activeType of SOURCE_TYPE_ORDER) {
    if (!hasModelSourceCore(source, activeType)) continue;
    const sanitized = buildSanitizedModelSourceByType(source, activeType);
    if (sanitized) return sanitized;
  }
  return undefined;
}

export function getModelSourceType(source?: Record<string, unknown>): MaasModelSourceType | undefined {
  if (!source) return undefined;
  return SOURCE_TYPE_ORDER.find((type) => hasModelSourceCore(source, type));
}

/** 从 sources 条目读取来源类型（扁平或嵌套 source 均支持） */
export function getModelSourceTypeFromEntry(entry?: MaasModelSourceEntry | null): MaasModelSourceType | undefined {
  if (!entry) return undefined;
  return getModelSourceType(extractSourceRecordFromEntry(entry as unknown as Record<string, unknown>));
}

/** 多源场景：以列表首条来源为准，后续模型须与其一致 */
export function resolveLockedModelSourceType(sources?: MaasModelSourceEntry[] | null): MaasModelSourceType | undefined {
  if (!sources?.length) return undefined;
  return getModelSourceTypeFromEntry(sources[0]);
}

export function areModelSourcesSameType(sources?: MaasModelSourceEntry[] | null): boolean {
  if (!sources?.length) return true;
  const locked = resolveLockedModelSourceType(sources);
  if (!locked) return true;
  return sources.every((entry) => {
    const type = getModelSourceTypeFromEntry(entry);
    return !type || type === locked;
  });
}

/** 规范化表单/API 的 sources 数组 */
export function normalizeModelSources(model?: MaasModel | null): MaasModelSourceEntry[] {
  if (!model || !Array.isArray(model.sources)) return [];
  return model.sources.filter((item) => item && typeof item === 'object') as MaasModelSourceEntry[];
}

/** 所有模型 size 之和（Gi） */
export function getTotalModelSourcesSizeGi(sources?: MaasModelSourceEntry[] | null): number | undefined {
  if (!sources?.length) return undefined;
  let sum = 0;
  let hasAny = false;
  for (const entry of sources) {
    const gi = parseStorageQuantityToGi(entry?.size);
    if (gi === undefined) continue;
    hasAny = true;
    sum += gi;
  }
  return hasAny ? sum : undefined;
}

/** 从 MaasModel 读取合计 Gi（供 PVC 最小容量等） */
export function getModelSizeGiFromModel(model?: MaasModel): number | undefined {
  return getTotalModelSourcesSizeGi(normalizeModelSources(model));
}

/** 已有 PVC 场景：仅服务名与大小，来源用占位 stackscope（提交前可再 sanitize） */
export function buildExistingPvcModelSourceEntry(name: string, size: string): MaasModelSourceEntry | undefined {
  const trimmedName = String(name || '').trim();
  const trimmedSize = String(size || '').trim();
  if (!trimmedName || !trimmedSize) return undefined;
  return { name: trimmedName, size: trimmedSize, ...buildExistingPvcModelSource(trimmedName) };
}

/** 将草稿（name/size + model.source）合并为 sources 条目 */
export function buildModelSourceEntryFromDraft(
  name: string,
  size: string,
  source: Record<string, unknown> | undefined,
): MaasModelSourceEntry | undefined {
  const sanitized = sanitizeModelSource(source);
  if (!sanitized) return undefined;
  const trimmedName = String(name || '').trim();
  const trimmedSize = String(size || '').trim();
  if (!trimmedName || !trimmedSize) return undefined;
  return {
    name: trimmedName,
    size: trimmedSize,
    ...sanitized,
  };
}

/** API 单条 source 序列化 */
export function serializeModelSourceEntryForApi(entry: MaasModelSourceEntry): Record<string, unknown> | undefined {
  const name = String(entry.name || '').trim();
  const size = String(entry.size || '').trim();
  const source = sanitizeModelSource(extractSourceRecordFromEntry(entry as unknown as Record<string, unknown>));
  if (!name || !size || !source) return undefined;
  return { name, size, ...source };
}

/** 列表/详情展示用：取首个服务名或拼接 */
export function formatModelDisplayNames(model?: MaasModel | null): string {
  const sources = normalizeModelSources(model);
  if (!sources.length) return '';
  const names = sources.map((s) => String(s.name || '').trim()).filter(Boolean);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  return `${names[0]} +${names.length - 1}`;
}

/** 将 API 条目转为表单条目（保留 name/size + 来源键） */
export function normalizeApiSourceEntry(raw: Record<string, unknown>): MaasModelSourceEntry | undefined {
  const name = String(raw.name ?? '').trim();
  const size = String(raw.size ?? '').trim();
  const source = sanitizeModelSource(extractSourceRecordFromEntry(raw));
  if (!name || !size) return undefined;
  if (!source) {
    return { name, size, ...(raw as MaasModelSource) };
  }
  return { name, size, ...source };
}
